import { motion } from 'framer-motion';
import type { StateCountyGeoBundle } from '../geo/useStateCountyGeo.hook';
import { matchGoogleCountyToTopoName } from '../geo/countyNameMatch.util';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import { CORAL, NAVY } from './tokens';

export interface CountyEngagementRow {
  name: string;
  memberCount: number;
}

export interface CountyEngagementMapProps {
  /** Projected county paths and centroids for the state. */
  geo: StateCountyGeoBundle;
  /** Warehouse county rows (sidebar + circle emphasis). */
  counties: CountyEngagementRow[];
}

/**
 * Renders a state county map with fill intensity and engagement circles by county count.
 * @param props - Geo bundle and county member counts.
 * @returns SVG map markup.
 */
const CountyEngagementMap = ({ geo, counties }: CountyEngagementMapProps) => {
  const printStatic = useReportsPrintStaticLayout();
  const max = Math.max(...counties.map(row => row.memberCount), 1);
  const topoNames = geo.paths.map(path => path.name);

  const engagedByTopoName = new Map<string, number>();
  counties.forEach(row => {
    const topoName = matchGoogleCountyToTopoName(row.name, topoNames);
    if (topoName) {
      engagedByTopoName.set(topoName, row.memberCount);
    }
  });

  const allEngagedValues = geo.paths
    .map(path => engagedByTopoName.get(path.name) ?? 0)
    .filter(count => count > 0);
  const mapMax = Math.max(...allEngagedValues, max, 1);

  return (
    <svg viewBox={geo.viewBox} className="w-full h-auto max-h-[320px]">
      <g>
        {geo.paths.map(pathItem => {
          const engaged = engagedByTopoName.get(pathItem.name) ?? 0;
          const fillOpacity =
            engaged > 0 ? 0.08 + (engaged / mapMax) * 0.32 : 0.04;
          return (
            <path
              key={pathItem.name}
              d={pathItem.d}
              fill={NAVY}
              fillOpacity={fillOpacity}
              stroke={NAVY}
              strokeOpacity={0.25}
              strokeWidth={0.25}
              strokeLinejoin="round"
            >
              {engaged > 0 && (
                <title>{`${pathItem.name}: ${engaged} members`}</title>
              )}
            </path>
          );
        })}
      </g>
      <g>
        {counties.map((row, index) => {
          const topoName = matchGoogleCountyToTopoName(row.name, topoNames);
          if (!topoName) {
            return null;
          }
          const centroid = geo.centroids[topoName];
          if (!centroid) {
            return null;
          }
          const [cx, cy] = centroid;
          const radius = 1.6 + (row.memberCount / max) * 4.2;
          const opacity = 0.55 + (row.memberCount / max) * 0.4;
          const label = row.name.replace(/\s*County$/i, '').trim();
          const circleProps = {
            cx,
            cy,
            r: radius,
            fill: CORAL,
            fillOpacity: opacity,
            stroke: CORAL,
            strokeOpacity: 1,
            strokeWidth: 0.3,
            children: <title>{`${label}: ${row.memberCount} members`}</title>,
          };

          if (printStatic) {
            return (
              <circle key={`${topoName}-${row.memberCount}`} {...circleProps} />
            );
          }

          return (
            <motion.circle
              key={`${topoName}-${row.memberCount}`}
              {...circleProps}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{
                duration: 0.5,
                delay: 0.15 + index * 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{ transformOrigin: `${cx}px ${cy}px` }}
            />
          );
        })}
      </g>
    </svg>
  );
};

export default CountyEngagementMap;
